import { useEffect, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import ModalComponent from "components/modal-component";
import "../../../../styles/modal.css";
import { useGetConversionRatesQuery } from "api/ruleEngineApi";
import { showToastError } from "components/toastify/ToastNotifications";
import Pagination from "rc-pagination/lib/Pagination";
import AddConversionRateModal from "./modals/AddConversionRateModal";
import EditConversionRateModal from "./modals/EditConversionRateModal";
import DeleteConversionRateModal from "./modals/DeleteConversionRateModal";
import { IoAddSharp } from "react-icons/io5";
import DeleteIcon from "assets/svg-icon/rule_engine/delete";
import EditIcon from "assets/svg-icon/rule_engine/edit";

interface ConversionRateData {
  id: string;
  application: string;
  fromCurrency: string;
  exchangeRate: number;
  toCurrency: string;
  costAmount: number;
  effectiveDate: string;
  createdBy: string;
  modifiedBy: string;
  createdAt: string;
  modifiedAt: string;
  versionCount: number;
}

interface CurrencyRuleResponse {
  totalPages: number;
  pageSize: number;
  pageNumber: number;
  totalSize: number;
  items: ConversionRateData[];
}

interface SubCompanyPagination {
  pageNumber: number;
  totalSize: number;
  pageSize: number;
}

const CurrencyConversions = () => {
  const [loading, setLoading] = useState(false);
  const [conversionRates, setConversionRates] = useState<ConversionRateData[]>(
    []
  );

  const [subCompanyPagination, setSubCompanyPagination] =
    useState<SubCompanyPagination>();
  const [showAddConversionRateModal, setShowAddConversionRateModal] =
    useState(false);
  const [showEditConversionRateModal, setShowEditConversionRateModal] =
    useState(false);
  const [selectedRate, setSelectedRate] = useState<ConversionRateData | null>(
    null
  );
  const [showDeleteConversionRateModal, setShowDeleteConversionRateModal] =
    useState(false);

  const handleDeleteClick = (rate: ConversionRateData) => {
    setSelectedRate(rate);
    setShowDeleteConversionRateModal(true);
  };

  const {
    data: conversionRateData,
    error: conversionRateError,
    isFetching: isFetchingConversionRate,
    refetch: refetchConversionRate,
  } = useGetConversionRatesQuery();

  useEffect(() => {
    if (isFetchingConversionRate) {
      setLoading(true);
    } else {
      setLoading(false);
      if (conversionRateError) {
        showToastError("Unable to fetch data from the server");
      } else if (conversionRateData?.data) {
        // Safe access to the items array
        const response = conversionRateData.data as CurrencyRuleResponse;
        setConversionRates(response.items || []);

        // Set pagination info
        setSubCompanyPagination({
          pageNumber: response.pageNumber,
          totalSize: response.totalSize,
          pageSize: response.pageSize,
        });

        
      } else {
        // Handle case where data is null/undefined
        setConversionRates([]);
      }
    }
  }, [isFetchingConversionRate, conversionRateError, conversionRateData]);

  const handleEditClick = (rule: ConversionRateData) => {
    setSelectedRate(rule);
    setShowEditConversionRateModal(true);
  };

  const onPageChange = (page: number) => {
    // Update the page number in state
    setSubCompanyPagination((prev) =>
      prev ? { ...prev, pageNumber: page } : undefined
    );
  };

  const formatExchangeRate = (
    exchangeRate: number,
    fromCurrency: string,
    toCurrency: string
  ) => {
    return `1 ${fromCurrency} = ${exchangeRate} ${toCurrency}`;
  };

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      marginTop: "1rem",
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "1.5rem",
    },
    headerTitle: {
      fontWeight: "bold",
      fontSize: "1.25rem",
      margin: 0,
      color: "#2c3e50",
    },
    headerActions: {
      display: "flex",
      alignItems: "center",
      gap: "0.75rem",
    },
    button: {
      padding: "0.375rem 0.75rem",
      fontSize: "0.875rem",
      border: "1px solid #ced4da",
      borderRadius: "0.25rem",
      backgroundColor: "white",
      color: "#495057",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
    },
    tableContainer: {
      backgroundColor: "white",
      borderRadius: "8px",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      position: "relative",
    },

    table: {
      width: "100%",
      borderCollapse: "collapse",
      fontSize: "14px",
    },
    thead: {
      backgroundColor: "rgba(255, 255, 255, 0.5)",
    },
    th: {
      padding: "16px 20px",
      fontWeight: "500",
      fontSize: "13px",
      color: "#6c757d",
      border: "none",
      textAlign: "left",
      borderBottom: "1px solid #e9ecef",
    },
    td: {
      padding: "20px",
      fontSize: "14px",
      color: "#6c757d",
      border: "none",
      verticalAlign: "middle",
      height: "115px",
    },
    versionBadge: {
      color: "#17171B",
      padding: "6px 12px",
      borderRadius: "20px",
      fontSize: "12px",
      fontWeight: "500",
      border: "1px solid #17171B",
      display: "inline-block",
      width: "62px",
      textAlign: "center",
    },
    // Empty state styles
    emptyState: {
      textAlign: "center",
      padding: "40px 20px",
      color: "#6c757d",
    },
    emptyStateIcon: {
      fontSize: "48px",
      marginBottom: "16px",
      color: "#dee2e6",
    },
    emptyStateText: {
      fontSize: "16px",
      fontWeight: "500",
      marginBottom: "8px",
    },
    emptyStateSubtext: {
      fontSize: "14px",
      color: "#adb5bd",
    },
  };

  return (
    <div>
      {loading ? (
        <div className="p-5 text-center">
          <Spinner />
        </div>
      ) : (
        <>
          <div
            className="d-flex justify-content-between align-items-start"
            style={{ marginTop: "56px" }}
          >
            <div>
              <h4 className="fw-bold mb-2" style={{ color: "#333" }}>
                Currency Conversion Rates
              </h4>
              <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                Manage EUR to USD and EUR to NGN conversion rates
              </p>
            </div>
            <div className="d-flex gap-2 mb-4">
              <Button
                variant="outline-secondary"
                size="sm"
                className="d-flex align-items-center gap-2"
                onClick={() => setShowAddConversionRateModal(true)}
                disabled={loading}
                style={{
                  border: "none",
                  padding: "13px 20px",
                  color: "#807E7C",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  backgroundColor: "#F2F2F2",
                }}
              >
                <IoAddSharp />
                Add Conversion Rate
              </Button>
              <ModalComponent
                showModal={showAddConversionRateModal}
                hideModal={() => setShowAddConversionRateModal(false)}
                dialogClassName="add-currency-rule-modal"
              >
                <AddConversionRateModal
                  activeModalTab="add-user"
                  onClose={() => setShowAddConversionRateModal(false)}
                  onSuccess={() => {
                    refetchConversionRate();
                    setShowAddConversionRateModal(false);
                  }}
                />
              </ModalComponent>
            </div>
          </div>

          <div className="mt-4">
            <div className="bg-white rounded">
              {/* Loading overlay */}
              {loading && (
                <div className="p-5 text-center">
                  <Spinner />
                </div>
              )}

              <div className="table-responsive">
                <table style={styles.table}>
                  <thead style={styles.thead}>
                    <tr>
                      <th style={styles.th}>S/N</th>
                      <th style={styles.th}>From Currency</th>
                      <th style={styles.th}>To Currency</th>
                      <th style={styles.th}>Exchange Rate</th>
                      <th style={styles.th}>Effective Date</th>
                      <th style={styles.th}>Last Updated</th>
                      <th style={styles.th}>Updated By</th>
                      <th style={styles.th}>Version</th>
                      <th style={styles.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!loading && conversionRates.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-5">
                          <div className="text-muted">
                            <div style={{ fontSize: "48px" }} className="mb-3">
                              📊
                            </div>
                            <div className="fw-medium mb-2">
                              No conversion rates found
                            </div>
                            <div>
                              Add your first conversion rate to get started
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      conversionRates.map((data, index) => (
                        <tr
                          key={index}
                          className="border-bottom"
                          style={{ color: "#17171B", fontSize: "14px" }}
                        >
                          <td className="px-4 py-4">{index + 1}</td>
                          <td className="px-4 py-4 fw-medium">
                            {data.fromCurrency}
                          </td>
                          <td className="px-4 py-4">{data.toCurrency}</td>
                          <td className="px-4 py-4">
                            {formatExchangeRate(
                              data.exchangeRate,
                              data.fromCurrency,
                              data.toCurrency
                            )}
                          </td>
                          <td className="px-4 py-4">{data.effectiveDate}</td>
                          <td className="px-4 py-4">
                            {data.modifiedAt
                              ? new Date(data.modifiedAt).toLocaleDateString()
                              : "-"}
                          </td>
                          <td className="px-4 py-4">
                            {data.createdBy || data.modifiedBy}
                          </td>
                          <td className="px-4 py-4">
                            <span style={styles.versionBadge}>
                              v{data.versionCount}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <button
                              className="btn btn-sm btn-link p-2"
                              title="Edit"
                              onClick={() => handleEditClick(data)}
                              disabled={loading}
                              style={{
                                border: "none",
                                backgroundColor: "transparent",
                              }}
                            >
                              <EditIcon />
                            </button>
                            <button
                              className="btn btn-sm btn-link p-2"
                              title="Delete"
                              onClick={() => handleDeleteClick(data)}
                              disabled={loading}
                            >
                              <DeleteIcon />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <ModalComponent
              showModal={showEditConversionRateModal}
              hideModal={() => setShowEditConversionRateModal(false)}
              dialogClassName="add-currency-rule-modal"
            >
              <EditConversionRateModal
                activeModalTab="add-user"
                onClose={() => setShowEditConversionRateModal(false)}
                selectedRate={selectedRate}
                onSuccess={() => {
                  refetchConversionRate();
                  setShowEditConversionRateModal(false);
                }}
              />
            </ModalComponent>

            <ModalComponent
              showModal={showDeleteConversionRateModal}
              hideModal={() => setShowDeleteConversionRateModal(false)}
              dialogClassName="add-currency-rule-modal"
            >
              <DeleteConversionRateModal
                show={showDeleteConversionRateModal}
                onClose={() => setShowDeleteConversionRateModal(false)}
                onDelete={() => {
                  refetchConversionRate();
                  setShowDeleteConversionRateModal(false);
                }}
                fromCurrency={selectedRate?.fromCurrency}
                toCurrency={selectedRate?.toCurrency}
                rateId={selectedRate?.id}
              />
            </ModalComponent>

            {!loading && conversionRates.length > 0 && (
              <div className="mt-4">
                <Pagination
                  current={subCompanyPagination?.pageNumber || 1}
                  total={subCompanyPagination?.totalSize || 0}
                  pageSize={subCompanyPagination?.pageSize || 10}
                  onChange={onPageChange}
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CurrencyConversions;
